import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, IconButton, useThemeValue } from '@altermaind/web/ui';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useWizard } from '@/_shared/hooks/useWizard';
import {
  getEmployeesImportsOverviewCall,
  postEmployeesImportsConfirmCall,
  postEmployeesImportsTemplateCall,
  postEmployeesImportsAbortCall,
} from '../../state/api';
import {
  DialogConfirmNavigateBack,
  DialogConfirmNavigateBackPayload,
} from './DialogConfirmNavigateBack';
import { EditImportedProfiles } from './EditImportedProfiles';

type Props = { wizardNavigationButton: WizardNavigationButton };

export const VerifyData: FC<Props> = ({ wizardNavigationButton }) => {
  const t = useTranslations('people.importCompanyData.verifyData.editImportedProfiles');
  const sharedT = useTranslations();
  const { openDialog } = useDialog<DialogConfirmNavigateBackPayload>(
    'uploadCompanyDataConfirmNavigateBack',
  );
  const navigate = useNavigate();

  const {
    setCustomOnPrevious,
    setCustomOnNext,
    setCustomOnSkip,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const { notify } = useNotification();
  const { execute: executePostEmployeesImportsConfirm } = postEmployeesImportsConfirmCall.hook();
  const { jobId } = postEmployeesImportsTemplateCall.hook();
  const { pendingReviewImport, reset: resetGetEmployeesImportsOverview } =
    getEmployeesImportsOverviewCall.hook();
  const { execute: executePostEmployeesImportsAbort } = postEmployeesImportsAbortCall.hook();

  const {
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm();
  const fieldValues = watch();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues).some((key: string) => !!fieldValues[key]);
  }, [fieldValues]);

  const onSubmit = useCallback(
    () => {
      if (jobId || pendingReviewImport?.pendingJob) {
        executePostEmployeesImportsConfirm({
          jobId: jobId || pendingReviewImport?.pendingJob || '',
          successSaga: () => {
            resetGetEmployeesImportsOverview();
            notify(t('successFeedback'), 'success');
            navigate(Routes.People);
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePostEmployeesImportsConfirm, pendingReviewImport?.pendingJob, jobId, sharedT],
  );

  const onAbortImport = useCallback(
    ({ silent = false }) => {
      if (jobId || pendingReviewImport?.pendingJob) {
        executePostEmployeesImportsAbort({
          jobId: (jobId || pendingReviewImport?.pendingJob) as string,
          successSaga: () => {
            resetGetEmployeesImportsOverview();
            if (!silent) {
              navigate(Routes.People);
              notify(sharedT('success.genericApiSuccess'), 'success');
            }
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      executePostEmployeesImportsAbort,
      navigate,
      pendingReviewImport?.pendingJob,
      resetGetEmployeesImportsOverview,
      sharedT,
      t,
      jobId,
    ],
  );

  const onPrevious: NonNullable<Parameters<typeof setCustomOnPrevious>[0]> = useCallback(
    navigateWizard => {
      openDialog({ navigate: navigateWizard, abort: () => onAbortImport({ silent: true }) });
    },
    [onAbortImport, openDialog],
  );

  useEffect(() => {
    setCustomOnNext(onSubmit);
    setCustomOnPrevious(onPrevious);

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [
    setCustomOnPrevious,
    handleSubmit,
    onSubmit,
    resetWizardNavigationCallbacks,
    setCustomOnNext,
    setCustomOnSkip,
    onPrevious,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: false,
      isLoadingNextButton: false,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [isValid, resetWizardNavigationButtonStatus, setNavigationButtonStatus, someInputFilled]);

  return (
    <Box mb='s'>
      <EditImportedProfiles />
      <DialogConfirmNavigateBack />
      <Box display='flex' style={{ gap: gapXS }} mt='m' justifyContent='flex-end'>
        {!pendingReviewImport?.pendingJob ? (
          <wizardNavigationButton.Previous />
        ) : (
          <IconButton
            prefixSvg='chevronLeft'
            variant='link/primary'
            onClick={() => onAbortImport({})}
            style={{ textDecoration: 'none', cursor: 'pointer' }}
            svgStyle={{ width: 'xs' }}
            textStyle={{ variant: 'p2/light', marginLeft: 'xxxxs' }}
            disabled={false}
          >
            {t('cta.deleteImportAndGoBack')}
          </IconButton>
        )}
        <wizardNavigationButton.Next />
      </Box>
    </Box>
  );
};
